/* ==========================================================================
   mensa · floral membership
   Drives the membership page: region toggle, tier selection, the three-step
   intake flow, and the hand-off to Shopify's cart / checkout.

   Data comes from JSON <script> tags rendered by the sections:
     #mm-regions-data  (membership-region-toggle)
     #mm-tiers-data    (membership-tiers)
     #mm-flow-data     (membership-flow)

   Loaded by several sections, so it guards against running twice.
   ========================================================================== */
(function () {
    'use strict';

    if (window.MensaMembership) return;

    var STORE_KEY = 'mm:state';
    var WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var MO = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var MO_SHORT = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    var MM = {
        regions: [],
        tiers: [],
        flow: null,
        state: {
            region: 0,
            tier: null,
            size: null,
            vase: null
        }
    };

    /* ---------- small helpers ------------------------------------------- */

    function $(sel, root) {
        return (root || document).querySelector(sel);
    }

    function $$(sel, root) {
        return Array.prototype.slice.call((root || document).querySelectorAll(sel));
    }

    function readJSON(id) {
        var el = document.getElementById(id);
        if (!el) return null;
        try {
            return JSON.parse(el.textContent);
        } catch (e) {
            return null;
        }
    }

    function save() {
        try {
            sessionStorage.setItem(STORE_KEY, JSON.stringify(MM.state));
        } catch (e) {
            /* private mode — state simply does not persist */
        }
    }

    function restore() {
        try {
            var raw = sessionStorage.getItem(STORE_KEY);
            if (raw) {
                var parsed = JSON.parse(raw);
                for (var k in parsed) {
                    if (Object.prototype.hasOwnProperty.call(parsed, k)) MM.state[k] = parsed[k];
                }
            }
        } catch (e) {
            /* ignore */
        }
    }

    /* Derives the shop's money format from a sample rendered by Liquid's `money`
       filter, so JS-built totals read exactly like Liquid-rendered prices in
       whichever currency the active market presents. */
    var moneyFmt = null;

    function parseMoneySample(sample) {
        var m = /\d[\d.,\s' ]*\d|\d/.exec(sample);
        if (!m) return null;
        var digits = m[0];
        var fmt = {
            prefix: sample.slice(0, m.index),
            suffix: sample.slice(m.index + digits.length),
            group: ',',
            decimal: '.',
            places: 0
        };
        // the last separator is the decimal point only if 1-2 digits follow it
        var tail = /[.,](\d{1,2})$/.exec(digits);
        if (tail) {
            fmt.decimal = digits.charAt(digits.length - tail[1].length - 1);
            fmt.places = tail[1].length;
        }
        var seps = digits.replace(/\d/g, '').split('');
        if (fmt.places) seps.pop();
        fmt.group = seps.length ? seps[seps.length - 1] : '';
        return fmt;
    }

    function formatAmount(cents, places, group, decimal) {
        var value = (cents / 100).toFixed(places);
        var parts = value.split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, group || '');
        return whole + (places ? decimal + parts[1] : '');
    }

    function money(cents) {
        if (!moneyFmt) {
            moneyFmt = (MM.flow && MM.flow.moneySample && parseMoneySample(MM.flow.moneySample)) || {
                prefix: ((MM.flow && MM.flow.currency) || 'AED') + ' ',
                suffix: '',
                group: ',',
                decimal: '.',
                places: 2
            };
        }

        // A region can display a converted currency with its own format. This is
        // presentation only — Shopify still charges the store currency at checkout,
        // which is what the region's conversion note is there to disclose.
        var r = region();
        var rate = parseFloat(r && r.rate);
        if (!isFinite(rate) || rate <= 0) rate = 1;
        var value = cents * rate;

        // Single braces on purpose: {{ }} in a JSON template setting is parsed by
        // Shopify as a dynamic source, so the money format cannot use that syntax.
        if (r && r.moneyFormat) {
            return r.moneyFormat
                .replace(/\{\s*amount_no_decimals\s*\}/g, formatAmount(value, 0, ',', '.'))
                .replace(/\{\s*amount\s*\}/g, formatAmount(value, 2, ',', '.'));
        }

        var f = moneyFmt;
        return f.prefix + formatAmount(value, f.places, f.group, f.decimal) + f.suffix;
    }

    function fmtDate(d) {
        return WD[d.getDay()] + ' ' + d.getDate() + ' ' + MO_SHORT[d.getMonth()] + ' ' + d.getFullYear();
    }

    function isWeekend(d) {
        var g = d.getDay();
        return g === 0 || g === 6;
    }

    /* Adds `days` business days, skipping weekends. */
    function addBusinessDays(from, days) {
        var d = new Date(from.getTime());
        var added = 0;
        while (added < days) {
            d.setDate(d.getDate() + 1);
            if (!isWeekend(d)) added++;
        }
        return d;
    }

    function startOfToday() {
        var d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    /* ---------- lookups --------------------------------------------------- */

    function region() {
        return MM.regions[MM.state.region] || MM.regions[0] || {};
    }

    function tier() {
        if (!MM.tiers.length) return null;
        var found = null;
        MM.tiers.forEach(function (t) {
            if (t.key === MM.state.tier) found = t;
        });
        return found || MM.tiers[0];
    }

    /* Sizes come from the selected product's variants, in the order the merchant
       set them. A "Size" block in the flow section only supplies the description
       shown under the name; a size with no block simply shows no description. */
    function sizeList() {
        var t = tier();
        if (!t || !t.variants) return [];
        var labels = (MM.flow && MM.flow.sizeLabels) || {};
        var seen = {};
        var list = [];
        t.variants.forEach(function (v) {
            if (!v.size) return;
            var lookup = String(v.size).toLowerCase();
            if (seen[lookup]) return;
            seen[lookup] = true;
            list.push({ key: v.size, label: labels[lookup] || '', available: isSellable(v) });
        });
        return list;
    }

    /* Membership arrangements are made to order. When the merchant leaves stock
       tracking on with zero on hand, every variant reads as unavailable, so the
       tiers section can tell us to ignore stock levels entirely. */
    function isSellable(variant) {
        if (!variant) return false;
        return MM.ignoreStock || variant.available;
    }

    /* The variant of the current tier that matches the chosen size. Falls back
       to the tier's only/first variant when the product has no size option. */
    function currentVariant() {
        var t = tier();
        if (!t || !t.variants || !t.variants.length) return null;
        var wanted = MM.state.size;
        var match = null;
        t.variants.forEach(function (v) {
            if (wanted && v.size && v.size.toLowerCase() === String(wanted).toLowerCase()) match = v;
        });
        return match || t.variants[0];
    }

    function variantForSize(t, sizeKey) {
        if (!t || !t.variants) return null;
        var match = null;
        t.variants.forEach(function (v) {
            if (v.size && v.size.toLowerCase() === String(sizeKey).toLowerCase()) match = v;
        });
        return match;
    }

    function vaseList() {
        if (!MM.flow || !MM.flow.vases) return [];
        return MM.flow.vases[String(MM.state.size).toLowerCase()] || [];
    }

    function currentVase() {
        if (!MM.state.vase) return null;
        var found = null;
        vaseList().forEach(function (v) {
            if (String(v.id) === String(MM.state.vase)) found = v;
        });
        return found;
    }

    /* ---------- screen + step routing ------------------------------------- */

    /* On mobile the theme parks its logo at the top of the viewport, exactly where
       the region bar sits. Publish both heights so the CSS can stack them the way
       the prototype did, instead of guessing offsets that break when the wordmark
       gains its "blooms" line or the pill shrinks at 600px. */
    function syncMobileHeader() {
        var root = document.documentElement;
        var bar = $('.mm-regionbar');
        var logo = $('.header-logo-wrapper');
        root.style.setProperty('--mm-bar-h', (bar ? bar.offsetHeight : 0) + 'px');
        root.style.setProperty('--mm-logo-h', (logo ? logo.offsetHeight : 0) + 'px');
        var pill = $('.mensa-pill');
        root.style.setProperty('--mm-pill-w', (pill ? pill.offsetWidth : 0) + 'px');
        root.style.setProperty('--mm-pill-h', (pill ? pill.offsetHeight : 0) + 'px');
    }

    function showScreen(name) {
        $$('[data-mm-screen]').forEach(function (el) {
            var on = el.getAttribute('data-mm-screen') === name;
            el.classList.toggle('mm-screen--active', on);
            // hide the whole section wrapper too, or its padding leaves a gap
            var host = el.closest('.shopify-section');
            if (host) host.style.display = on ? '' : 'none';
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    var STEPS = ['space', 'size'];

    function currentStep() {
        var active = $('[data-mm-step].mm-screen--active');
        return active ? active.getAttribute('data-mm-step') : null;
    }

    function showStep(name) {
        showScreen('flow');
        $$('[data-mm-step]').forEach(function (el) {
            el.classList.toggle('mm-screen--active', el.getAttribute('data-mm-step') === name);
        });
        $$('[data-mm-crumb]').forEach(function (el) {
            el.classList.toggle('mm-crumb--on', el.getAttribute('data-mm-crumb') === name);
        });
        if (name === 'space') renderIntake();
        if (name === 'size') { renderSizeStep(); renderSummary(); }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /* ---------- region ----------------------------------------------------- */

    function applyRegion() {
        var r = region();

        $$('[data-mm-region-toggle]').forEach(function (el) {
            el.setAttribute('data-mm-index', String(MM.state.region));
            $$('[data-mm-region-opt]', el).forEach(function (opt, i) {
                opt.classList.toggle('mm-region__opt--on', i === MM.state.region);
            });
        });

        $$('[data-mm-whatsapp]').forEach(function (el) {
            el.setAttribute('data-mm-number', r.whatsapp || '');
        });

        var regionInput = $('[data-mm-prop="region"]');
        if (regionInput) regionInput.value = r.label || '';

        var note = $('[data-mm-conversion-note]');
        if (note) {
            note.textContent = r.conversionNote || '';
            note.style.display = r.conversionNote ? '' : 'none';
        }

        // every price on the page is region-dependent once conversion is in play
        renderTierPrices();
        renderSizeStep();
        renderSummary();
    }

    /* Tier prices are rendered by Liquid, so they get rewritten in place when the
       region — and therefore the displayed currency — changes. */
    function renderTierPrices() {
        MM.tiers.forEach(function (t) {
            var el = $('[data-mm-tier-price="' + t.key + '"]');
            if (el && typeof t.priceMin === 'number') el.textContent = money(t.priceMin);
        });
    }

    function setRegion(index) {
        if (index === MM.state.region) return;
        MM.state.region = index;
        save();
        applyRegion();
    }

    function escapeHTML(s) {
        return String(s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    function escapeAttr(s) {
        return escapeHTML(s);
    }

    /* ---------- pickers ----------------------------------------------------- */

    function closeAllPickers(except) {
        $$('.mm-picker').forEach(function (p) {
            if (p !== except) p.classList.remove('mm-picker--open');
        });
    }

    function setPickerValue(picker, value, label) {
        var valEl = $('[data-mm-picker-val]', picker);
        var placeholder = picker.getAttribute('data-mm-placeholder') || '';
        if (valEl) {
            valEl.textContent = label || placeholder;
            valEl.classList.toggle('mm-picker__val--empty', !label);
        }
        var propName = picker.getAttribute('data-mm-prop-target');
        if (propName) {
            var input = $('[data-mm-prop="' + propName + '"]');
            if (input) input.value = value;
        }
        picker.classList.remove('mm-invalid');
    }

    function initPickers() {
        document.addEventListener('click', function (e) {
            var field = e.target.closest ? e.target.closest('.mm-picker__field') : null;
            if (field) {
                var picker = field.closest('.mm-picker');
                var wasOpen = picker.classList.contains('mm-picker--open');
                closeAllPickers();
                if (!wasOpen) picker.classList.add('mm-picker--open');
                e.stopPropagation();
                return;
            }

            var opt = e.target.closest ? e.target.closest('.mm-opt') : null;
            if (opt) {
                var p = opt.closest('.mm-picker');
                var pop = opt.closest('.mm-picker__pop');
                if (pop) {
                    $$('.mm-opt', pop).forEach(function (o) { o.classList.remove('mm-opt--sel'); });
                }
                opt.classList.add('mm-opt--sel');
                var value = opt.getAttribute('data-mm-value');
                setPickerValue(p, value === null ? opt.textContent.trim() : value, opt.textContent.trim());
                p.classList.remove('mm-picker--open');
                e.stopPropagation();
                return;
            }

            if (!e.target.closest || !e.target.closest('.mm-picker__pop')) closeAllPickers();
        });
    }

    /* ---------- calendars ---------------------------------------------------- */

    function initCalendar(picker) {
        var daysEl = $('[data-mm-cal-days]', picker);
        var titleEl = $('[data-mm-cal-title]', picker);
        if (!daysEl) return;

        var leadDays = parseInt(picker.getAttribute('data-mm-lead-days'), 10);
        if (isNaN(leadDays)) leadDays = 7;
        var min = startOfToday();
        min.setDate(min.getDate() + leadDays);
        picker._min = min;

        var view = { y: min.getFullYear(), m: min.getMonth() };

        function render() {
            titleEl.textContent = MO[view.m] + ' ' + view.y;
            daysEl.innerHTML = '';
            var first = new Date(view.y, view.m, 1).getDay();
            var dim = new Date(view.y, view.m + 1, 0).getDate();
            var i;
            for (i = 0; i < first; i++) {
                var blank = document.createElement('div');
                blank.className = 'mm-cal__day mm-cal__day--blank';
                daysEl.appendChild(blank);
            }
            for (var d = 1; d <= dim; d++) {
                var dt = new Date(view.y, view.m, d);
                var cell = document.createElement('button');
                cell.type = 'button';
                cell.className = 'mm-cal__day';
                cell.textContent = String(d);
                if (dt < picker._min) {
                    cell.className += ' mm-cal__day--dis';
                } else {
                    cell.setAttribute('data-mm-cal-date', dt.getTime());
                    if (picker._date && picker._date.getTime() === dt.getTime()) {
                        cell.className += ' mm-cal__day--sel';
                    }
                }
                daysEl.appendChild(cell);
            }
        }

        picker._render = render;

        $('[data-mm-cal-prev]', picker).addEventListener('click', function (e) {
            e.stopPropagation();
            view.m--;
            if (view.m < 0) { view.m = 11; view.y--; }
            render();
        });
        $('[data-mm-cal-next]', picker).addEventListener('click', function (e) {
            e.stopPropagation();
            view.m++;
            if (view.m > 11) { view.m = 0; view.y++; }
            render();
        });

        daysEl.addEventListener('click', function (e) {
            var cell = e.target.closest('[data-mm-cal-date]');
            if (!cell) return;
            e.stopPropagation();
            var picked = new Date(parseInt(cell.getAttribute('data-mm-cal-date'), 10));
            setCalendarDate(picker, picked);
            picker.classList.remove('mm-picker--open');
            if (picker.getAttribute('data-mm-picker') === 'visit-date') syncDerivedDelivery();
        });

        render();
    }

    function setCalendarDate(picker, date) {
        picker._date = date;
        setPickerValue(picker, fmtDate(date), fmtDate(date));
        if (picker._render) picker._render();
    }

    /* Premium: the first delivery lands a set number of business days after the
       home visit, so the delivery calendar is filled in and locked. */
    function syncDerivedDelivery() {
        var t = tier();
        var visit = $('[data-mm-picker="visit-date"]');
        var delivery = $('[data-mm-picker="delivery-date"]');
        var lead = $('[data-mm-delivery-lead]');
        var derived = $('[data-mm-delivery-derived]');
        if (!delivery) return;

        if (t && t.requiresVisit && visit && visit._date) {
            var gap = MM.flow && MM.flow.visitToDeliveryDays ? MM.flow.visitToDeliveryDays : 3;
            var date = addBusinessDays(visit._date, gap);
            setCalendarDate(delivery, date);
            delivery.classList.add('mm-picker--locked');
            if (lead) lead.style.display = 'none';
            if (derived) {
                derived.style.display = '';
                derived.textContent = 'Your first delivery is set for ' + fmtDate(date) +
                    ' — ' + gap + ' business days after your home visit.';
            }
        } else {
            delivery.classList.remove('mm-picker--locked');
            if (lead) lead.style.display = '';
            if (derived) derived.style.display = 'none';
        }
    }

    /* ---------- tiers -------------------------------------------------------- */

    function selectTier(key) {
        MM.state.tier = key;
        MM.state.size = defaultSize();
        MM.state.vase = null;
        save();
        showStep('space');
    }

    /* ---------- step: your space ---------------------------------------------- */

    function renderIntake() {
        var t = tier();
        var premium = !!(t && t.requiresVisit);

        var label = $('[data-mm-tier-label]');
        if (label && t) label.textContent = t.name;

        var standard = $('[data-mm-intake="standard"]');
        var visit = $('[data-mm-intake="visit"]');
        if (standard) showIntakeBranch(standard, !premium);
        if (visit) showIntakeBranch(visit, premium);
    }

    /* Both intake branches share property names, so the hidden one is disabled
       to keep it out of the form post. */
    function showIntakeBranch(branch, on) {
        branch.style.display = on ? '' : 'none';
        $$('input, textarea, select', branch).forEach(function (el) {
            el.disabled = !on;
        });
    }

    function initPhotoDrop() {
        $$('[data-mm-photos]').forEach(function (drop) {
            var inputs = $$('input[type="file"]', drop);
            var summary = $('[data-mm-photos-summary]', drop);
            var list = $('[data-mm-photos-list]', drop);

            function refresh() {
                var count = 0;
                var nextEmpty = null;
                if (list) list.innerHTML = '';
                inputs.forEach(function (inp) {
                    inp.classList.remove('mm-file--active');
                    if (inp.files && inp.files[0]) {
                        count++;
                        if (list && /^image\//.test(inp.files[0].type)) {
                            var img = document.createElement('img');
                            img.src = URL.createObjectURL(inp.files[0]);
                            img.onload = function () { URL.revokeObjectURL(img.src); };
                            list.appendChild(img);
                        }
                    } else if (!nextEmpty) {
                        nextEmpty = inp;
                    }
                });
                // the next empty slot is the one the drop zone opens
                if (nextEmpty) nextEmpty.classList.add('mm-file--active');
                drop.classList.toggle('mm-drop--filled', count > 0);
                drop.classList.remove('mm-invalid');
                if (summary) {
                    summary.innerHTML = count > 0
                        ? '&#10003; ' + count + (count === 1 ? ' photograph added' : ' photographs added') +
                          '<br><span class="mm-hint">' +
                          (nextEmpty ? 'tap to add another' : 'all slots used') + '</span>'
                        : summary.getAttribute('data-mm-default');
                }
            }

            inputs.forEach(function (input) {
                input.addEventListener('change', refresh);
            });
            refresh();
        });
    }

    /* ---------- step: size + vase ---------------------------------------------- */

    /* The first sellable size of the current tier. */
    function defaultSize() {
        var sizes = sizeList();
        if (!sizes.length) return null;
        var pick = null;
        sizes.forEach(function (s) {
            if (!pick && s.available) pick = s.key;
        });
        return pick || sizes[0].key;
    }

    function renderSizeStep() {
        var t = tier();
        var sizes = sizeList();
        var wrap = $('[data-mm-size-choices]');
        var label = $('[data-mm-size-label]');

        // a product with no size option has nothing to choose here
        if (label) label.style.display = sizes.length ? '' : 'none';
        if (wrap) wrap.style.display = sizes.length ? '' : 'none';

        // the size carried over from another tier may not exist on this product
        if (sizes.length && !variantForSize(t, MM.state.size)) MM.state.size = defaultSize();

        if (wrap) {
            wrap.innerHTML = '';
            sizes.forEach(function (s) {
                var v = variantForSize(t, s.key);
                var selected = String(MM.state.size).toLowerCase() === String(s.key).toLowerCase();
                var btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'mm-choice' + (selected ? ' mm-choice--on' : '');
                btn.setAttribute('data-mm-size', s.key);
                if (!s.available) btn.setAttribute('disabled', 'disabled');
                var html = '<span class="mm-choice__cap">' + escapeHTML(s.key) + '</span>' + escapeHTML(s.label);
                if (v && MM.flow.showSizePrices) {
                    html += '<span class="mm-choice__price">' +
                        (s.available ? money(v.price) + ' / month' : MM.flow.unavailableLabel || 'currently unavailable') +
                        '</span>';
                }
                btn.innerHTML = html;
                wrap.appendChild(btn);
            });
        }
        renderVases();
        syncDerivedDelivery();
        writeSizeProperty();
    }

    function setSize(key) {
        MM.state.size = key;
        MM.state.vase = null;
        save();
        renderSizeStep();
        renderSummary();
    }

    function writeSizeProperty() {
        var input = $('[data-mm-prop="size"]');
        if (input) input.value = MM.state.size || '';
    }

    function renderVases() {
        var picker = $('[data-mm-picker="vase"]');
        if (!picker) return;
        var pop = $('.mm-picker__pop', picker);
        var list = vaseList();
        var ownLabel = MM.flow.vaseOwnLabel || 'I have my own vase';

        if (!list.length) {
            picker.style.display = 'none';
            var vaseLabelEl = $('[data-mm-vase-label]');
            if (vaseLabelEl) vaseLabelEl.style.display = 'none';
            return;
        }
        picker.style.display = '';

        var html = '<button type="button" class="mm-vase-own' + (!MM.state.vase ? ' mm-vase--sel' : '') +
            '" data-mm-vase="">' + escapeHTML(ownLabel) + '</button><div class="mm-vase-grid">';
        list.forEach(function (v) {
            html += '<button type="button" class="mm-vase-card' +
                (String(MM.state.vase) === String(v.id) ? ' mm-vase--sel' : '') +
                '" data-mm-vase="' + escapeAttr(v.id) + '">' +
                '<span class="mm-vase-card__img">' +
                (v.image ? '<img src="' + escapeAttr(v.image) + '" alt="' + escapeAttr(v.name) + '">' : '') +
                '</span><span class="mm-vase-card__meta">' +
                '<span class="mm-vase-card__name">' + escapeHTML(v.name) + '</span>' +
                (v.dimension ? '<span class="mm-vase-card__dim">' + escapeHTML(v.dimension) + '</span>' : '') +
                '<span class="mm-vase-card__price">' + money(v.price) + '</span>' +
                '</span></button>';
        });
        html += '</div>';
        pop.innerHTML = html;

        var v = currentVase();
        setPickerValue(picker, v ? v.name : '', v ? v.name + ' · ' + money(v.price) : '');
        if (!v) {
            var valEl = $('[data-mm-picker-val]', picker);
            if (valEl) {
                valEl.textContent = ownLabel;
                valEl.classList.remove('mm-picker__val--empty');
            }
        }
    }

    function setVase(id) {
        MM.state.vase = id || null;
        save();
        renderVases();
        renderSummary();
        var picker = $('[data-mm-picker="vase"]');
        if (picker) picker.classList.remove('mm-picker--open');
    }

    /* ---------- running total on the size step ------------------------------------ */

    function renderSummary() {
        var t = tier();
        if (!t) return;
        var variant = currentVariant();
        var vase = currentVase();
        var r = region();

        var monthly = variant ? variant.price : 0;
        var vasePrice = vase ? vase.price : 0;

        var suffix = MM.flow.summarySuffix ? ' ' + MM.flow.summarySuffix : '';
        setText('[data-mm-sum-tier]', t.name + suffix);
        setText('[data-mm-sum-price]', money(monthly));
        setText('[data-mm-sum-vase]', vase
            ? vase.name + ' · ' + money(vasePrice) + ' once'
            : (MM.flow.vaseOwnSummary || 'own vase — no charge'));
        setText('[data-mm-sum-total]', money(monthly + vasePrice));

        setText('[data-mm-coverage-note]', r.coverage || '');

        var fine = $('[data-mm-sum-fine]');
        if (fine) {
            fine.textContent = (vasePrice > 0 ? 'Includes your vase, charged once today. ' : '') +
                'Then ' + money(monthly) + ' each month — pause or cancel anytime.';
        }

        var submitLabel = $('[data-mm-submit-label]');
        if (submitLabel) submitLabel.textContent = (MM.flow.submitLabel || 'Continue to checkout');
    }

    function setText(sel, value) {
        var el = $(sel);
        if (el) el.textContent = value;
    }

    /* ---------- validation --------------------------------------------------------- */

    function validate(step, mutate) {
        var ok = true;

        function mark(el, valid) {
            if (mutate && el) el.classList.toggle('mm-invalid', !valid);
            if (!valid) ok = false;
        }

        function filled(sel) {
            var el = $(sel);
            return !!(el && el.value.trim() !== '');
        }

        function dateSet(sel) {
            var p = $(sel);
            return !!(p && p._date);
        }

        var msg = null;

        if (step === 'space') {
            var t = tier();
            if (t && t.requiresVisit) {
                msg = $('[data-mm-msg="visit"]');
                mark($('[data-mm-picker="visit-date"]'), dateSet('[data-mm-picker="visit-date"]'));
                mark($('[data-mm-picker="visit-time"]'), filled('[data-mm-prop="visit_time"]'));
                mark($('[data-mm-field="visit_area"]'), filled('[data-mm-field="visit_area"]'));
            } else {
                msg = $('[data-mm-msg="space"]');
                var drop = $('[data-mm-photos]');
                if (MM.flow.requirePhotos) mark(drop, !!(drop && drop.classList.contains('mm-drop--filled')));
                mark($('[data-mm-field="palette"]'), filled('[data-mm-field="palette"]'));
            }
        } else if (step === 'size') {
            msg = $('[data-mm-msg="size"]');
            mark($('[data-mm-picker="delivery-date"]'), dateSet('[data-mm-picker="delivery-date"]'));
            var variant = currentVariant();
            if (!isSellable(variant)) {
                ok = false;
                if (mutate && msg) msg.textContent = 'That size is currently unavailable — please choose another.';
            } else if (mutate && msg) {
                msg.textContent = msg.getAttribute('data-mm-default') || msg.textContent;
            }
        }

        if (mutate && msg) msg.classList.toggle('mm-formmsg--show', !ok);
        return ok;
    }

    function canJumpTo(target) {
        var ti = STEPS.indexOf(target);
        var ai = STEPS.indexOf(currentStep());
        if (ti < 0 || ti <= ai) return true;
        var okAll = true;
        for (var i = 0; i < ti; i++) {
            if (!validate(STEPS[i], false)) okAll = false;
        }
        if (!okAll) validate(currentStep(), true);
        return okAll;
    }

    /* ---------- submit → Shopify cart ------------------------------------------------ */

    function submitMembership(form) {
        if (!validate('space', false)) { showStep('space'); validate('space', true); return; }
        if (!validate('size', true)) return;

        var variant = currentVariant();
        var msg = $('[data-mm-msg="size"]');
        if (!variant || !isSellable(variant)) {
            if (msg) {
                msg.textContent = MM.flow.cartError || 'That size is currently unavailable.';
                msg.classList.add('mm-formmsg--show');
            }
            return;
        }

        var btn = $('[data-mm-submit]');
        if (btn) btn.setAttribute('aria-busy', 'true');

        // hidden fields the form posts to /cart/add
        setInput('[data-mm-variant-input]', variant.id);
        var planInput = $('[data-mm-sellingplan-input]');
        if (planInput) {
            if (variant.sellingPlan) {
                planInput.value = variant.sellingPlan;
                planInput.disabled = false;
            } else {
                planInput.disabled = true;
            }
        }
        writeSizeProperty();
        setInput('[data-mm-prop="tier"]', tier() ? tier().name : '');

        // drop empty properties, anything in the intake branch that is not on screen,
        // and anything scoped to the other kind of intake, so none of it clutters the order
        var mode = tier() && tier().requiresVisit ? 'visit' : 'standard';
        $$('[name^="properties["]', form).forEach(function (el) {
            var branch = el.closest('[data-mm-intake]');
            if (branch && branch.style.display === 'none') {
                el.disabled = true;
                return;
            }
            var only = el.getAttribute('data-mm-only');
            if (only && only !== mode) {
                el.disabled = true;
                return;
            }
            if (el.type === 'file') {
                el.disabled = !(el.files && el.files.length);
            } else {
                el.disabled = el.value.trim() === '';
            }
        });

        var vase = currentVase();
        if (!vase) {
            form.submit();
            return;
        }

        // a vase is a separate one-time line item, so it goes in first over AJAX
        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart/add.js'
            : '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                items: [{
                    id: vase.id,
                    quantity: 1,
                    properties: { 'Paired with': (tier() ? tier().name : '') + ' — ' + (MM.state.size || '') }
                }]
            })
        }).then(function () {
            form.submit();
        }).catch(function () {
            form.submit();
        });
    }

    function setInput(sel, value) {
        var el = $(sel);
        if (el) el.value = value;
    }

    /* ---------- wiring ------------------------------------------------------------------- */

    function bind() {
        document.addEventListener('click', function (e) {
            var el = e.target.closest ? e.target : null;
            if (!el || !el.closest) return;

            var regionOpt = el.closest('[data-mm-region-opt]');
            if (regionOpt) {
                var opts = $$('[data-mm-region-opt]', regionOpt.closest('[data-mm-region-toggle]'));
                setRegion(opts.indexOf(regionOpt));
                return;
            }
            var regionToggle = el.closest('[data-mm-region-toggle]');
            if (regionToggle) {
                setRegion(MM.state.region === 0 ? 1 : 0);
                return;
            }

            var tierBtn = el.closest('[data-mm-select-tier]');
            if (tierBtn) {
                e.preventDefault();
                selectTier(tierBtn.getAttribute('data-mm-select-tier'));
                return;
            }

            var sizeBtn = el.closest('[data-mm-size]');
            if (sizeBtn) {
                setSize(sizeBtn.getAttribute('data-mm-size'));
                return;
            }

            var vaseBtn = el.closest('[data-mm-vase]');
            if (vaseBtn) {
                e.stopPropagation();
                setVase(vaseBtn.getAttribute('data-mm-vase'));
                return;
            }

            var choice = el.closest('[data-mm-choice]');
            if (choice) {
                var group = choice.closest('[data-mm-choice-group]');
                $$('[data-mm-choice]', group).forEach(function (c) { c.classList.remove('mm-choice--on'); });
                choice.classList.add('mm-choice--on');
                var target = group.getAttribute('data-mm-choice-group');
                var input = $('[data-mm-prop="' + target + '"]');
                if (input) input.value = choice.getAttribute('data-mm-choice');
                return;
            }

            var crumb = el.closest('[data-mm-crumb]');
            if (crumb) {
                var to = crumb.getAttribute('data-mm-crumb');
                if (canJumpTo(to)) showStep(to);
                return;
            }

            var nav = el.closest('[data-mm-go]');
            if (nav) {
                e.preventDefault();
                var dest = nav.getAttribute('data-mm-go');
                if (dest === 'membership') {
                    showScreen('membership');
                    return;
                }
                if (nav.hasAttribute('data-mm-advance') && !validate(currentStep(), true)) return;
                showStep(dest);
                return;
            }

            var submitBtn = el.closest('[data-mm-submit]');
            if (submitBtn) {
                e.preventDefault();
                var form = $('[data-mm-form]');
                if (form) submitMembership(form);
                return;
            }

            var wa = el.closest('[data-mm-whatsapp]');
            if (wa) {
                var number = (wa.getAttribute('data-mm-number') || '').replace(/\D/g, '');
                var text = encodeURIComponent(wa.getAttribute('data-mm-message') || '');
                if (number) window.open('https://wa.me/' + number + (text ? '?text=' + text : ''), '_blank');
                return;
            }

            if (el.closest('[data-mm-terms-open]')) {
                var modal = $('[data-mm-terms-modal]');
                if (modal) modal.classList.add('mm-modal--open');
                return;
            }
            if (el.closest('[data-mm-terms-close]') || el.hasAttribute('data-mm-terms-modal')) {
                var m = $('[data-mm-terms-modal]');
                if (m) m.classList.remove('mm-modal--open');
            }
        });

        document.addEventListener('input', function (e) {
            if (e.target.classList) e.target.classList.remove('mm-invalid');
        });

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                closeAllPickers();
                var m = $('[data-mm-terms-modal]');
                if (m) m.classList.remove('mm-modal--open');
            }
        });

        var form = $('[data-mm-form]');
        if (form) {
            form.addEventListener('submit', function (e) {
                if (!form.hasAttribute('data-mm-ready')) {
                    e.preventDefault();
                    form.setAttribute('data-mm-ready', 'true');
                    submitMembership(form);
                }
            });
        }
    }

    /* ---------- boot ------------------------------------------------------------------------ */

    function init() {
        var regionData = readJSON('mm-regions-data');
        var tiersData = readJSON('mm-tiers-data');
        var flowData = readJSON('mm-flow-data');

        MM.regions = (regionData && regionData.regions) || [];
        MM.tiers = (tiersData && tiersData.tiers) || [];
        MM.ignoreStock = !!(tiersData && tiersData.ignoreStock);
        MM.flow = flowData || {};

        restore();
        if (MM.state.region >= MM.regions.length) MM.state.region = 0;
        if (!MM.state.tier && MM.tiers.length) MM.state.tier = MM.tiers[0].key;
        if (!variantForSize(tier(), MM.state.size)) MM.state.size = defaultSize();

        initPickers();
        initPhotoDrop();
        $$('.mm-picker--date').forEach(initCalendar);
        bind();

        applyRegion();
        renderIntake();
        renderSizeStep();
        renderSummary();

        // A picker that renders a default option shows that label straight away,
        // but nothing has written it to the property input yet. Seed it, or the
        // answer silently never reaches the order.
        $$('.mm-picker[data-mm-prop-target]').forEach(function (picker) {
            var selected = $('.mm-opt--sel', picker);
            if (!selected) return;
            var input = $('[data-mm-prop="' + picker.getAttribute('data-mm-prop-target') + '"]');
            if (input && !input.value) {
                input.value = selected.getAttribute('data-mm-value') || selected.textContent.trim();
            }
        });

        // preselect any choice groups that start with a default
        $$('[data-mm-choice-group]').forEach(function (group) {
            var on = $('[data-mm-choice].mm-choice--on', group);
            if (!on) on = $('[data-mm-choice]', group);
            if (!on) return;
            on.classList.add('mm-choice--on');
            var input = $('[data-mm-prop="' + group.getAttribute('data-mm-choice-group') + '"]');
            if (input && !input.value) input.value = on.getAttribute('data-mm-choice');
        });

        showScreen('membership');

        // the SVG wordmark settles after load, so measure again then
        syncMobileHeader();
        window.addEventListener('load', syncMobileHeader);
        window.addEventListener('resize', syncMobileHeader);
    }

    MM.showScreen = showScreen;
    MM.showStep = showStep;
    window.MensaMembership = MM;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
